import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type SubmitHandler, useForm, useFormState } from "react-hook-form";
import { useDispatch } from "react-redux";

import { sourcesApiSlice } from "api/sourcesApi.slice";
import { TextInput, UIButton, UISwitch } from "common/components";
import { ApiErrorMessage } from "common/components/ApiErrorMessage/ApiErrorMessage";
import { Select } from "common/components/form/Select";
import { logger } from "common/utilities/logger";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getSiteLanguage } from "domains/language/selectors";
import { activateModal } from "domains/modal/actions";
import {
  COUNTRY_TO_REGION_MAPPING,
  REGION_TO_GREATER_REGION_MAPPING,
} from "domains/moderate/sources/components/EditSourceForm/constants";
import * as Styled from "domains/moderate/sources/components/EditSourceForm/EditSourceForm.styled";
import { SourceLogoImagePicker } from "domains/moderate/sources/components/SourceLogoImagePicker/SourceLogoImagePicker";
import { type SourceFormData, sourceFormDataSchema } from "domains/moderate/sources/components/types";
import {
  AUTOPILOT_OPTIONS,
  CONTENT_TYPE_OPTIONS,
  COUNTRY_OPTIONS,
  type CountryValue,
  GREATER_REGION_OPTIONS,
  LICENSE_OPTIONS,
  PUBLICATION_TYPE_OPTIONS,
  REGION_OPTIONS,
  SOURCE_TYPE_OPTIONS,
} from "domains/moderate/sources/constants";

type EditSourceFormProps = {
  sourceId: string;
  onCancel: () => void;
  onSubmitSuccess: () => void;
};

export const EditSourceForm = ({ sourceId, onCancel, onSubmitSuccess }: EditSourceFormProps) => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const siteLanguage = useSelector(getSiteLanguage);

  const { data: source } = sourcesApiSlice.endpoints.getSourceById.useQuery({ sourceId });
  const [updateSource, { isLoading, error }] = sourcesApiSlice.endpoints.updateSource.useMutation();

  const { control, handleSubmit, setValue } = useForm({
    resolver: zodResolver(sourceFormDataSchema),
    defaultValues: {
      name: source?.name ?? "",
      url: source?.url ?? "",
      description: source?.description ?? "",
      active: source?.active ?? true,
      logoUrl: source?.favicon,
      license: LICENSE_OPTIONS.find((option) => option.value === source?.license),
      autopilot: AUTOPILOT_OPTIONS.find((option) => option.value === source?.auto_approved),
      type: SOURCE_TYPE_OPTIONS.find((option) => option.value === source?.type),
      publicationType: PUBLICATION_TYPE_OPTIONS.find((option) => option.value === source?.publication_type),
      contentType: CONTENT_TYPE_OPTIONS.find((option) => option.value === source?.content_type),
      country: COUNTRY_OPTIONS.find((option) => option.value === source?.country),
      region: REGION_OPTIONS.find((option) => option.value === source?.region),
      greaterRegion: GREATER_REGION_OPTIONS.find((option) => option.value === source?.greater_region),
    },
  });

  const { isDirty } = useFormState({ control });

  const setRegionAndGreaterRegionOnCountryChange = (country: CountryValue) => {
    const associatedRegion = COUNTRY_TO_REGION_MAPPING[country];
    const associatedGreaterRegion = REGION_TO_GREATER_REGION_MAPPING[associatedRegion];

    setValue(
      "region",
      REGION_OPTIONS.find((option) => option.value === associatedRegion),
    );

    setValue(
      "greaterRegion",
      GREATER_REGION_OPTIONS.find((option) => option.value === associatedGreaterRegion),
    );
  };

  const onSubmit: SubmitHandler<SourceFormData> = async (data) => {
    if (!isDirty) {
      onSubmitSuccess?.();
      return;
    }

    if (isLoading) return;

    const result = await updateSource({
      sourceId,
      name: data.name,
      url: data.url,
      description: data.description,
      active: data.active,
      logoUrl: data.logoUrl,
      license: data.license?.value,
      autopilot: data.autopilot?.value ?? null,
      type: data.type?.value,
      publicationType: data.publicationType?.value,
      contentType: data.contentType?.value,
      country: data.country?.value,
      region: data.region?.value,
      greaterRegion: data.greaterRegion?.value,
    });

    if (result.error) {
      logger.error("[EditSourceForm] Error updating source", error);
      return;
    }

    onSubmitSuccess?.();
  };

  const handleCloseButtonPress = () => {
    if (!isDirty) {
      onCancel();
      return;
    }

    dispatch(activateModal({ id: "ConfirmCloseEditSourceForm", params: { onCloseModal: onCancel } }));
  };

  return (
    <Styled.Form
      key={siteLanguage}
      onSubmit={handleSubmit(onSubmit, (error) => logger.error("[EditSourceForm] Error submitting form", error))}
    >
      <ApiErrorMessage error={error} />
      <Styled.FormBody>
        <Styled.FormBodyGridColumn>
          <Controller
            control={control}
            name="name"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="name">
                  {translate("app.moderate.sources.name.label")}
                  <Styled.RequiredAsterisk> *</Styled.RequiredAsterisk>
                </Styled.Label>
                <TextInput name="name" value={value} onChange={onChange} hasError={!!error} />
                <Styled.ErrorMessage>{error?.message}</Styled.ErrorMessage>
              </Styled.FormFieldContainer>
            )}
          />
          <Controller
            control={control}
            name="description"
            render={({ field: { value, onChange } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="description">{translate("app.moderate.sources.description.label")}</Styled.Label>
                <TextInput name="description" value={value} onChange={onChange} />
              </Styled.FormFieldContainer>
            )}
          />
          <Controller
            control={control}
            name="url"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="url">
                  {translate("app.moderate.sources.url.label")}
                  <Styled.RequiredAsterisk> *</Styled.RequiredAsterisk>
                </Styled.Label>
                <TextInput name="url" value={value} onChange={onChange} hasError={!!error} />
                <Styled.ErrorMessage>{error?.message}</Styled.ErrorMessage>
              </Styled.FormFieldContainer>
            )}
          />
          <Controller
            control={control}
            name="active"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="status-switch">
                  {translate("app.moderate.sources.status.label")}
                  <Styled.RequiredAsterisk> *</Styled.RequiredAsterisk>
                </Styled.Label>
                <Styled.StatusSwitchContainer>
                  <Styled.StatusTag isActive={value}>{value ? "Active" : "Inactive"}</Styled.StatusTag>
                  <UISwitch
                    id="status-switch"
                    isOn={value}
                    onClick={(e) => {
                      e.preventDefault();
                      onChange(!value);
                    }}
                  />
                </Styled.StatusSwitchContainer>
                <Styled.ErrorMessage>{error?.message}</Styled.ErrorMessage>
              </Styled.FormFieldContainer>
            )}
          />
          <Controller
            control={control}
            name="logoUrl"
            render={({ field: { value, onChange } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="logo-url-picker">{translate("app.moderate.sources.logo.label")}</Styled.Label>
                <SourceLogoImagePicker
                  accessibilityId="logo-url-picker"
                  imageUrl={value}
                  onSetImageUrl={(imageUrl) => {
                    sourcesApiSlice.util.updateQueryData("getSourceById", { sourceId }, (draft) => {
                      draft.favicon = imageUrl;
                    });
                    onChange(imageUrl);
                  }}
                />
              </Styled.FormFieldContainer>
            )}
          />
          <Controller
            control={control}
            name="license"
            render={({ field: { value, onChange } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="license-select">{translate("app.moderate.sources.license.label")}</Styled.Label>
                <Select
                  accessibilityId="license-select"
                  value={value}
                  placeholder={translate("app.moderate.sources.license.placeholder")}
                  options={LICENSE_OPTIONS.map((option) => ({
                    ...option,
                    onClick: () => onChange(option.value === value?.value ? undefined : option),
                  }))}
                />
              </Styled.FormFieldContainer>
            )}
          />
          <Controller
            control={control}
            name="autopilot"
            render={({ field: { value, onChange } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="autopilot-select">
                  {translate("app.moderate.sources.autopilot.label")}
                </Styled.Label>
                <Select
                  accessibilityId="autopilot-select"
                  value={value}
                  placeholder={translate("app.moderate.sources.autopilot.placeholder")}
                  options={AUTOPILOT_OPTIONS.map((option) => ({
                    ...option,
                    onClick: () => onChange(option.value === value?.value ? undefined : option),
                  }))}
                />
              </Styled.FormFieldContainer>
            )}
          />
        </Styled.FormBodyGridColumn>
        <Styled.FormBodyGridColumn>
          <Controller
            control={control}
            name="type"
            render={({ field: { value, onChange } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="type-select">{translate("app.moderate.sources.type.label")}</Styled.Label>
                <Select
                  accessibilityId="type-select"
                  placeholder={translate("app.moderate.sources.type.placeholder")}
                  value={value}
                  options={SOURCE_TYPE_OPTIONS.map((option) => ({
                    ...option,
                    onClick: () => onChange(option.value === value?.value ? undefined : option),
                  }))}
                />
              </Styled.FormFieldContainer>
            )}
          />
          <Controller
            control={control}
            name="contentType"
            render={({ field: { value, onChange } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="content-type-select">
                  {translate("app.moderate.sources.contentType.label")}
                </Styled.Label>
                <Select
                  accessibilityId="content-type-select"
                  placeholder={translate("app.moderate.sources.contentType.placeholder")}
                  value={value}
                  options={CONTENT_TYPE_OPTIONS.map((option) => ({
                    ...option,
                    onClick: () => onChange(option.value === value?.value ? undefined : option),
                  }))}
                />
              </Styled.FormFieldContainer>
            )}
          />
          <Controller
            control={control}
            name="publicationType"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="publication-type-select">
                  {translate("app.moderate.sources.publicationType.label")}
                  <Styled.RequiredAsterisk> *</Styled.RequiredAsterisk>
                </Styled.Label>
                <Select
                  accessibilityId="publication-type-select"
                  value={value}
                  options={PUBLICATION_TYPE_OPTIONS.map((option) => ({
                    ...option,
                    onClick: () => onChange(option),
                  }))}
                  hasError={!!error}
                />
                <Styled.ErrorMessage>{error?.message}</Styled.ErrorMessage>
              </Styled.FormFieldContainer>
            )}
          />
          <Controller
            control={control}
            name="country"
            render={({ field: { value, onChange } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="country-type-select">
                  {translate("app.moderate.sources.country.label")}
                </Styled.Label>
                <Select
                  accessibilityId="country-type-select"
                  placeholder={translate("app.moderate.sources.country.placeholder")}
                  value={value}
                  options={COUNTRY_OPTIONS.map((option) => ({
                    ...option,
                    onClick: () => {
                      onChange(option.value === value?.value ? undefined : option);
                      setRegionAndGreaterRegionOnCountryChange(option.value);
                    },
                  }))}
                />
              </Styled.FormFieldContainer>
            )}
          />
          <Controller
            control={control}
            name="region"
            render={({ field: { value, onChange } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="region-type-select">
                  {translate("app.moderate.sources.region.label")}
                </Styled.Label>
                <Select
                  accessibilityId="region-type-select"
                  placeholder={translate("app.moderate.sources.region.placeholder")}
                  value={value}
                  options={REGION_OPTIONS.map((option) => ({
                    ...option,
                    onClick: () => onChange(option.value === value?.value ? undefined : option),
                  }))}
                  isDisabled
                />
              </Styled.FormFieldContainer>
            )}
          />
          <Controller
            control={control}
            name="greaterRegion"
            render={({ field: { value, onChange } }) => (
              <Styled.FormFieldContainer>
                <Styled.Label htmlFor="greater-region-type-select">
                  {translate("app.moderate.sources.greaterRegion.label")}
                </Styled.Label>
                <Select
                  accessibilityId="greater-region-type-select"
                  placeholder={translate("app.moderate.sources.greaterRegion.placeholder")}
                  value={value}
                  options={GREATER_REGION_OPTIONS.map((option) => ({
                    ...option,
                    onClick: () => onChange(option.value === value?.value ? undefined : option),
                  }))}
                  isDisabled
                />
              </Styled.FormFieldContainer>
            )}
          />
        </Styled.FormBodyGridColumn>
      </Styled.FormBody>
      <Styled.ButtonsContainer>
        <UIButton type="submit">{translate("app.moderate.sources.confirmEdits.button")}</UIButton>
        <UIButton
          as="button"
          variant="outlined"
          onClick={(event) => {
            event.preventDefault();
            handleCloseButtonPress();
          }}
        >
          {translate("app.moderate.sources.cancel.button")}
        </UIButton>
      </Styled.ButtonsContainer>
    </Styled.Form>
  );
};
